/**
 * SEO Sprint 2 (Feb 2026) — Breadcrumbs UI + BreadcrumbList schema.
 *
 * One thin component that renders both:
 *   - de visuele breadcrumb-trail (mobile-friendly, geen layout-breuk)
 *   - de bijbehorende JSON-LD `BreadcrumbList` (via <Schema /> uit
 *     `Schema.jsx` zodat schema en visuele weergave synchroon zijn).
 *
 * Props
 * -----
 *   items:    [{ name: string, url?: string }]
 *              Home → … → currentPage. Het laatste item heeft meestal
 *              geen `url` (current page) zoals Google's BreadcrumbList
 *              spec aanraadt.
 *   testid:   optionele test-id (default: "breadcrumbs")
 *   className: optionele extra Tailwind classes (zelden nodig)
 *
 * Gebruikt nooit `<h1>` of `<h2>` → geen dubbele heading.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Home as HomeIcon } from "lucide-react";
import Schema from "./Schema";

const ORIGIN = (process.env.REACT_APP_PUBLIC_ORIGIN
  || "https://www.5999cargo.nl").replace(/\/$/, "");

function absoluteUrl(url) {
  if (!url) return undefined;
  if (/^https?:/.test(url)) return url;
  return `${ORIGIN}${url.startsWith("/") ? "" : "/"}${url}`;
}

export default function Breadcrumbs({ items = [], testid = "breadcrumbs", className = "" }) {
  if (!Array.isArray(items) || items.length === 0) return null;

  // Schema items always include the absolute URL when known; the current
  // page's `url` is optional in schema.org BreadcrumbList.
  const schemaItems = items.map((it) => ({
    name: it.name || "",
    url: absoluteUrl(it.url),
  }));

  return (
    <>
      <Schema type="BreadcrumbList" data={{ items: schemaItems }} />
      <nav
        aria-label="Breadcrumb"
        data-testid={testid}
        className={`text-xs sm:text-sm ${className}`}
      >
        <ol className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-gray-600">
          {items.map((it, idx) => {
            const isLast = idx === items.length - 1;
            const isFirst = idx === 0;
            return (
              <li
                key={`${it.name}-${idx}`}
                className="inline-flex items-center gap-1.5 max-w-[60vw] sm:max-w-none"
              >
                {idx > 0 ? (
                  <ChevronRight
                    className="w-3.5 h-3.5 text-gray-400 flex-shrink-0"
                    aria-hidden="true"
                  />
                ) : null}
                {isLast || !it.url ? (
                  <span
                    aria-current={isLast ? "page" : undefined}
                    className="truncate font-medium text-[#082c50]"
                    data-testid={`${testid}-item-${idx}`}
                  >
                    {isFirst ? (
                      <HomeIcon className="w-3.5 h-3.5 inline mr-1 -mt-0.5" aria-hidden="true" />
                    ) : null}
                    {it.name}
                  </span>
                ) : (
                  <Link
                    to={it.url}
                    className="truncate text-gray-500 hover:text-[#082c50] underline-offset-4 hover:underline transition-colors"
                    data-testid={`${testid}-item-${idx}`}
                  >
                    {isFirst ? (
                      <HomeIcon className="w-3.5 h-3.5 inline mr-1 -mt-0.5" aria-hidden="true" />
                    ) : null}
                    {it.name}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    </>
  );
}
